import { api } from './api'

export interface DataPortabilityExportRequest {
  patientId?: string // If omitted, exports entire practice population
  format: 'csv' | 'text'
  includeFiles?: boolean // Always true for data portability
}

/**
 * Data Portability Export Service
 * Handles exporting patient data for accreditation compliance
 * Admin-only service
 */
export const dataPortabilityService = {
  /**
   * Export patient data for data portability (accreditation compliance)
   * @param request - Export request with patientId (optional), format, and includeFiles
   * @returns Promise that resolves when download is triggered
   */
  exportDataPortability: async (request: DataPortabilityExportRequest): Promise<void> => {
    try {
      const response = await api.post('/reports/data-portability', request, {
        responseType: 'blob',
      })

      // Generate filename from request
      const extension = request.format === 'text' ? 'txt' : 'csv'
      const timestamp = new Date().toISOString().split('T')[0]
      const filename = request.patientId
        ? `patient-data-portability-${request.patientId}-${timestamp}.${extension}`
        : `practice-data-portability-${timestamp}.${extension}`

      // Trigger download
      const url = window.URL.createObjectURL(response.data as Blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error: any) {
      console.error('Error exporting data portability:', error)
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to export patient data'
      
      // Provide more helpful error messages
      if (error.response?.status === 404) {
        throw new Error(`Export endpoint not found. Please ensure the reports service is deployed.`)
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Admin role required for data export.')
      } else {
        throw new Error(errorMessage)
      }
    }
  },
}
